import React from 'react';
import { Link } from 'react-router-dom';
import { useSectionContent } from '../hooks/useSiteContent';
import useJsonLd from '../hooks/useJsonLd';
import { ORGANIZATION_SCHEMA } from '../seo/schemaOrg';

// Default footer content (BlueLock Freights brand; CMS overrides at runtime)
const FOOTER_DEFAULTS = {
  footer_company_description: "BlueLock Freights operates as a trade/brand name associated with BlueLock Transport LLC, a Michigan-based motor carrier headquartered in Grandville, Michigan. We operate with a focus on regulatory compliance, operational consistency, and long-term driver partnerships.",
  footer_location: "Grandville, Michigan",
  footer_legal_name: "BlueLock Transport LLC",
  footer_authority: "USDOT 4557330 · MC 1810883",
  footer_address: "4370 Chicago Drive SW, Grandville, MI 49418",
  footer_phone: "616-438-6969",
  footer_copyright_line: "© BlueLock Transport LLC. All rights reserved."
};

const PublicFooter = () => {
  const { content } = useSectionContent('footer', FOOTER_DEFAULTS);
  const currentYear = new Date().getFullYear();
  useJsonLd('organization', ORGANIZATION_SCHEMA);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">BL</span>
              </div>
              <div>
                <span className="text-xl font-bold">BlueLock</span>
                <span className="text-xl font-light text-gray-400 ml-1">Freights</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed max-w-md">
              {content.footer_company_description}
            </p>
            <div className="mt-4 text-gray-500 text-sm space-y-1">
              <p data-testid="footer-legal-name">{content.footer_legal_name}</p>
              <p data-testid="footer-address">{content.footer_address}</p>
              <p data-testid="footer-phone">{content.footer_phone}</p>
              <p data-testid="footer-authority" className="text-gray-600">{content.footer_authority}</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-300 hover:text-white text-sm transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-white text-sm transition-colors" data-testid="footer-link-about">
                  About
                </Link>
              </li>
              <li>
                <Link to="/drivers" className="text-gray-300 hover:text-white text-sm transition-colors">
                  Driver Opportunities
                </Link>
              </li>
              <li>
                <Link to="/apply" className="text-gray-300 hover:text-white text-sm transition-colors">
                  Apply
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-300 hover:text-white text-sm transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-white text-sm transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-gray-300 hover:text-white text-sm transition-colors">
                  Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Programs
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/programs" className="text-gray-300 hover:text-white text-sm transition-colors" data-testid="footer-link-programs">
                  All Programs
                </Link>
              </li>
              <li>
                <Link to="/lease-on" className="text-gray-300 hover:text-white text-sm transition-colors" data-testid="footer-link-lease-on">
                  Lease-On
                </Link>
              </li>
              <li>
                <Link to="/dispatch-services" className="text-gray-300 hover:text-white text-sm transition-colors" data-testid="footer-link-dispatch-services">
                  Dispatch-Only
                </Link>
              </li>
              <li>
                <Link to="/company-driver" className="text-gray-300 hover:text-white text-sm transition-colors" data-testid="footer-link-company-driver">
                  Company Driver
                </Link>
              </li>
              <li>
                <Link to="/policies" className="text-gray-300 hover:text-white text-sm transition-colors">
                  Policies
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm" data-testid="footer-copyright">
              {content.footer_copyright_line.replace(/©\s*/, '© ' + currentYear + ' ')}
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/drivers" className="hover:text-gray-300 transition-colors">
                Driver Opportunities
              </Link>
              <Link to="/apply" className="hover:text-gray-300 transition-colors">
                Apply
              </Link>
              <Link to="/login" className="hover:text-gray-300 transition-colors">
                Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
